(() => {
  const CONSENT_KEY = "dutchxxl_cookie_consent_meta";
  const LEGACY_CONSENT_KEY = "shopify_cookie_consent_status";
  const CONSENT_VERSION = "2.0";
  const SIX_MONTHS = 158112e5;
  const CONSENT_PURPOSES = ["preferences", "analytics", "marketing"];

  let customerPrivacyReady;
  let listenersBound = false;

  const getBanner = () => document.getElementById("cookieConsent");
  const getPreferencesPanel = () =>
    document.querySelector("[data-consent-preferences]");
  const getOverview = () => document.querySelector("[data-consent-overview]");

  const getStoredJson = (key) => {
    try {
      const data = window.localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  };

  const setStoredJson = (key, value) => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {}
  };

  const removeStoredItem = (key) => {
    try {
      window.localStorage.removeItem(key);
    } catch {}
  };

  const getConsentMeta = () => getStoredJson(CONSENT_KEY);

  const isConsentMetaCurrent = (data) =>
    data?.version === CONSENT_VERSION &&
    data?.timestamp &&
    Date.now() - data.timestamp <= SIX_MONTHS;

  const hasRecordedVisitorConsent = (consent) =>
    CONSENT_PURPOSES.some(
      (key) => consent?.[key] === "yes" || consent?.[key] === "no",
    );

  const choicesFromInputs = () =>
    CONSENT_PURPOSES.reduce((choices, purpose) => {
      choices[purpose] = Boolean(
        document.querySelector(`[data-consent-input="${purpose}"]`)?.checked,
      );
      return choices;
    }, {});

  const allChoices = (value) => ({
    preferences: value,
    analytics: value,
    marketing: value,
  });

  const currentAllowedChoices = () =>
    CONSENT_PURPOSES.reduce((choices, purpose) => {
      choices[purpose] = purposeAllowed(purpose);
      return choices;
    }, {});

  const setInputChoices = (choices) => {
    CONSENT_PURPOSES.forEach((purpose) => {
      const input = document.querySelector(`[data-consent-input="${purpose}"]`);
      if (input) input.checked = Boolean(choices?.[purpose]);
    });
  };

  const saveConsentMeta = (choices) => {
    setStoredJson(CONSENT_KEY, {
      version: CONSENT_VERSION,
      timestamp: Date.now(),
      choices,
    });
    removeStoredItem(LEGACY_CONSENT_KEY);
  };

  const storedChoicesAllowed = (purpose) => {
    const meta = getConsentMeta();
    return isConsentMetaCurrent(meta) && meta.choices?.[purpose] === true;
  };

  const purposeAllowed = (purpose) => {
    const privacy = window.Shopify?.customerPrivacy;
    if (purpose === "preferences" && privacy?.preferencesProcessingAllowed) {
      return privacy.preferencesProcessingAllowed();
    }
    if (purpose === "analytics" && privacy?.analyticsProcessingAllowed) {
      return privacy.analyticsProcessingAllowed();
    }
    if (purpose === "marketing" && privacy?.marketingAllowed) {
      return privacy.marketingAllowed();
    }

    const visitorConsent = privacy?.currentVisitorConsent?.();
    if (visitorConsent?.[purpose] === "yes") return true;
    if (visitorConsent?.[purpose] === "no") return false;

    return storedChoicesAllowed(purpose);
  };

  const cleanupConsentBoundStorage = (choices) => {
    if (!choices.preferences) {
      removeStoredItem("predictiveSearchHistory");
    }
  };

  const updateGoogleConsentMode = (choices) => {
    if (typeof window.gtag !== "function") return;

    const analytics = choices.analytics ? "granted" : "denied";
    const marketing = choices.marketing ? "granted" : "denied";
    const preferences = choices.preferences ? "granted" : "denied";

    window.gtag("consent", "update", {
      analytics_storage: analytics,
      ad_storage: marketing,
      ad_user_data: marketing,
      ad_personalization: marketing,
      functionality_storage: preferences,
      personalization_storage: preferences,
      security_storage: "granted",
    });
  };

  const loadCustomerPrivacyApi = () => {
    if (customerPrivacyReady) return customerPrivacyReady;

    customerPrivacyReady = new Promise((resolve) => {
      if (!window.Shopify?.loadFeatures) {
        resolve();
        return;
      }

      window.Shopify.loadFeatures(
        [{ name: "consent-tracking-api", version: "0.1" }],
        () => resolve(),
      );
    });

    return customerPrivacyReady;
  };

  const hideBanner = () => {
    const banner = getBanner();
    if (banner) banner.classList.add("cookie-consent--hidden");
  };

  const openPreferences = () => {
    const panel = getPreferencesPanel();
    const overview = getOverview();
    const trigger = document.querySelector("[data-consent-open-preferences]");

    if (panel) panel.hidden = false;
    if (overview) overview.hidden = true;
    if (trigger) trigger.setAttribute("aria-expanded", "true");
  };

  const closePreferences = () => {
    const panel = getPreferencesPanel();
    const overview = getOverview();
    const trigger = document.querySelector("[data-consent-open-preferences]");

    if (panel) panel.hidden = true;
    if (overview) overview.hidden = false;
    if (trigger) trigger.setAttribute("aria-expanded", "false");
  };

  const showBanner = ({ preferences = false } = {}) => {
    const banner = getBanner();
    if (!banner) return;

    setInputChoices(currentAllowedChoices());
    banner.classList.remove("cookie-consent--hidden");

    if (preferences) {
      openPreferences();
      getPreferencesPanel()?.querySelector("button, input, a")?.focus();
    } else {
      closePreferences();
    }
  };

  const shouldShowBanner = () => {
    const meta = getConsentMeta();
    if (isConsentMetaCurrent(meta)) return false;
    if (meta && !isConsentMetaCurrent(meta)) return true;
    if (getStoredJson(LEGACY_CONSENT_KEY) && !isConsentMetaCurrent(meta)) return true;

    const privacy = window.Shopify?.customerPrivacy;
    if (privacy?.shouldShowBanner) {
      try {
        return privacy.shouldShowBanner();
      } catch {}
    }

    const visitorConsent = privacy?.currentVisitorConsent?.();
    if (hasRecordedVisitorConsent(visitorConsent)) return false;

    return !isConsentMetaCurrent(meta);
  };

  const finishConsent = (choices, previousChoices) => {
    const withdrewOptionalConsent = CONSENT_PURPOSES.some(
      (purpose) => previousChoices?.[purpose] && !choices[purpose],
    );

    saveConsentMeta(choices);
    cleanupConsentBoundStorage(choices);
    updateGoogleConsentMode(choices);
    hideBanner();

    if (withdrewOptionalConsent) {
      window.setTimeout(() => window.location.reload(), 150);
      return;
    }
  };

  const recordConsent = (choices) => {
    const privacy = window.Shopify?.customerPrivacy;
    const previousChoices = currentAllowedChoices();
    let finished = false;
    const finishOnce = () => {
      if (finished) return;
      finished = true;
      finishConsent(choices, previousChoices);
    };

    if (privacy?.setTrackingConsent) {
      window.setTimeout(finishOnce, 2500);
      privacy.setTrackingConsent(
        {
          preferences: choices.preferences,
          analytics: choices.analytics,
          marketing: choices.marketing,
        },
        finishOnce,
      );
    } else {
      finishOnce();
    }
  };

  const handleAction = (action) => {
    if (action === "accept") {
      recordConsent(allChoices(true));
      return;
    }

    if (action === "reject") {
      recordConsent(allChoices(false));
      return;
    }

    if (action === "save") {
      recordConsent(choicesFromInputs());
    }
  };

  const bindListeners = () => {
    if (listenersBound) return;
    listenersBound = true;

    document.addEventListener("click", (event) => {
      const preferencesTrigger = event.target.closest("[data-cookie-preferences]");
      if (preferencesTrigger) {
        event.preventDefault();
        loadCustomerPrivacyApi().then(() => showBanner({ preferences: true }));
        return;
      }

      const openTrigger = event.target.closest("[data-consent-open-preferences]");
      if (openTrigger) {
        openPreferences();
        return;
      }

      if (event.target.closest("[data-consent-close-preferences]")) {
        closePreferences();
        return;
      }

      const actionButton = event.target.closest("[data-consent-action]");
      if (actionButton) handleAction(actionButton.dataset.consentAction);
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !getPreferencesPanel()?.hidden) {
        closePreferences();
      }
    });
  };

  const initializeCookieConsent = () => {
    bindListeners();
    if (shouldShowBanner()) showBanner();
  };

  window.DutchXXLConsent = {
    allows: purposeAllowed,
    openPreferences: () =>
      loadCustomerPrivacyApi().then(() => showBanner({ preferences: true })),
  };

  const scheduleInit = () =>
    loadCustomerPrivacyApi().then(initializeCookieConsent);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleInit, { once: true });
  } else {
    scheduleInit();
  }
})();
